import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number as USD currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

/**
 * Format a date for display
 */
export function formatDate(dateInput: string | Date | any): string {
  try {
    let date: Date;
    
    if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else if (dateInput && typeof dateInput === 'object') {
      if (typeof (dateInput as any).toDate === 'function') {
        date = (dateInput as any).toDate();
      } else if (typeof (dateInput as any)._seconds === 'number') {
        const seconds = (dateInput as any)._seconds;
        const nanoseconds = (dateInput as any)._nanoseconds || 0;
        date = new Date(seconds * 1000 + nanoseconds / 1000000);
      } else {
        throw new Error('Unrecognized date object format');
      }
    } else {
      throw new Error('Invalid date format');
    }
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date value');
    }
    
    // Use UTC methods to avoid timezone shifts for date-only displays
    const year = date.getFullYear();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    
    return `${month} ${day}, ${year}`;
  } catch (error) {
    return 'Unknown date';
  }
}

/**
 * Format carrier names using Firebase data
 */
export async function formatCarrierName(carrierCode: string): Promise<string> {
  try {
    // Dynamic import to avoid circular dependency
    const { getCarrierByCode } = await import('../services/carrierService');
    const carrier = await getCarrierByCode(carrierCode);
    return carrier ? carrier.name : carrierCode.toUpperCase();
  } catch (error) {
    return carrierCode.toUpperCase();
  }
}

/**
 * Format carrier names synchronously (fallback only)
 * This should only be used when Firebase data is not immediately available
 */
export function formatCarrierNameSync(carrierCode: string): string {
  // Simple fallback - just capitalize the code
  return carrierCode.toUpperCase();
}
